/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/* Copyright 2026 Bytefinger Technology (HK) Co., Limited. SPDX-License-Identifier: Apache-2.0 */
import {
  useEffect,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
} from 'react';

import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type { ResearchTaskLabels } from './research-task-copy';
import { isTerminalResearchTaskStatus } from './research-task-copy';

export type ResearchTaskCancelStatus =
  | 'requested'
  | 'confirmed'
  | 'unconfirmed'
  | 'unsupported';

export interface ResearchTaskView {
  task_id: string;
  status: string;
  stage?: string;
  cancel_status?: ResearchTaskCancelStatus;
  progress?: string;
  billing_status?: string;
  partial_text?: string;
  partial_updated_at?: number;
  report?: {
    title: string;
    body: string;
    model: string;
    coverage?: string;
    citations: Array<{ title: string; url: string }>;
    artifact?: { id: string; url: string };
  };
  error?: { code?: string; message?: string };
}

const loginRequiredStatus = 401;
const notFoundStatus = 404;
const basePollIntervalMs = 2000;
const maxPollIntervalMs = 30000;
const pollBackoffMultiplier = 2;

class ResearchTaskAccessError extends Error {
  constructor(readonly status: number) {
    super('Research task unavailable');
  }
}

async function readResearchTask(
  taskId: string,
  signal: AbortSignal,
): Promise<ResearchTaskView> {
  const response = await fetch(
    `/api/research/tasks/${encodeURIComponent(taskId)}`,
    { credentials: 'same-origin', signal },
  );
  if (
    response.status === loginRequiredStatus ||
    response.status === notFoundStatus
  ) {
    throw new ResearchTaskAccessError(response.status);
  }
  if (!response.ok) {
    throw new Error('Research task unavailable');
  }
  const task = (await response.json()) as ResearchTaskView;
  if (task.status === 'completed' && task.report?.artifact) {
    const reportResponse = await fetch(
      `/api/research/tasks/${encodeURIComponent(taskId)}/report`,
      { credentials: 'same-origin', signal },
    );
    if (
      reportResponse.status === loginRequiredStatus ||
      reportResponse.status === notFoundStatus
    ) {
      throw new ResearchTaskAccessError(reportResponse.status);
    }
    if (!reportResponse.ok) {
      throw new Error('Research report unavailable');
    }
    task.report = await reportResponse.json();
  }
  return task;
}

function accessErrorMessage(
  error: unknown,
  login: string,
  unavailable: string,
) {
  if (error instanceof ResearchTaskAccessError) {
    return error.status === loginRequiredStatus ? login : unavailable;
  }
  return unavailable;
}

function hasMeaningfulTaskUpdate(
  previous: ResearchTaskView | undefined,
  next: ResearchTaskView,
) {
  if (!previous) {
    return true;
  }
  return (
    previous.status !== next.status ||
    previous.stage !== next.stage ||
    previous.cancel_status !== next.cancel_status ||
    previous.progress !== next.progress ||
    previous.billing_status !== next.billing_status ||
    previous.partial_text !== next.partial_text ||
    previous.partial_updated_at !== next.partial_updated_at ||
    JSON.stringify(previous.report) !== JSON.stringify(next.report) ||
    JSON.stringify(previous.error) !== JSON.stringify(next.error)
  );
}

interface TaskPollingState {
  dataUpdateCount: number;
  errorUpdateCount: number;
  intervalMs: number;
  previousTask?: ResearchTaskView;
}

function increasePollInterval(intervalMs: number) {
  return Math.min(intervalMs * pollBackoffMultiplier, maxPollIntervalMs);
}

function canRefreshTask(task: ResearchTaskView | undefined, error: unknown) {
  return (
    !isTerminalResearchTaskStatus(task?.status ?? '') &&
    !(error instanceof ResearchTaskAccessError)
  );
}

export function useResearchTask(taskId: string, label: ResearchTaskLabels) {
  const queryClient = useQueryClient();
  const queryKey = ['research-task', taskId] as const;
  const pollingStateRef = useRef<TaskPollingState>({
    dataUpdateCount: 0,
    errorUpdateCount: 0,
    intervalMs: basePollIntervalMs,
  });
  const lastDataUpdatedAtRef = useRef(0);
  const [deleted, setDeleted] = useState(false);
  const [actionError, setActionError] = useState('');
  const taskQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) => readResearchTask(taskId, signal),
    enabled: !deleted,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    notifyOnChangeProps: 'all',
    refetchOnMount: true,
    refetchOnWindowFocus: query =>
      canRefreshTask(query.state.data, query.state.error),
    refetchOnReconnect: query =>
      canRefreshTask(query.state.data, query.state.error),
    refetchIntervalInBackground: false,
    refetchInterval: query => {
      const task = query.state.data;
      const pollingState = pollingStateRef.current;
      // QueryObserver re-evaluates this callback after each result; counts keep
      // repeated evaluations from advancing the backoff more than once.
      if (query.state.dataUpdateCount > pollingState.dataUpdateCount) {
        const hasUpdate = task
          ? hasMeaningfulTaskUpdate(pollingState.previousTask, task)
          : false;
        pollingState.previousTask = task;
        pollingState.dataUpdateCount = query.state.dataUpdateCount;
        pollingState.errorUpdateCount = query.state.errorUpdateCount;
        pollingState.intervalMs = hasUpdate
          ? basePollIntervalMs
          : increasePollInterval(pollingState.intervalMs);
      } else if (query.state.errorUpdateCount > pollingState.errorUpdateCount) {
        const firstTransientFailure =
          query.state.dataUpdateCount === 0 &&
          pollingState.errorUpdateCount === 0;
        pollingState.errorUpdateCount = query.state.errorUpdateCount;
        if (!firstTransientFailure) {
          pollingState.intervalMs = increasePollInterval(
            pollingState.intervalMs,
          );
        }
      }
      if (
        deleted ||
        query.state.error instanceof ResearchTaskAccessError ||
        (task && isTerminalResearchTaskStatus(task.status))
      ) {
        return false;
      }
      return pollingState.intervalMs;
    },
  });
  useEffect(() => {
    if (taskQuery.dataUpdatedAt > lastDataUpdatedAtRef.current) {
      lastDataUpdatedAtRef.current = taskQuery.dataUpdatedAt;
      setActionError('');
    }
  }, [taskQuery.dataUpdatedAt]);

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        `/api/research/tasks/${encodeURIComponent(taskId)}/cancel`,
        { method: 'POST', credentials: 'same-origin' },
      );
      if (!response.ok) {
        throw new ResearchTaskAccessError(response.status);
      }
      return (await response.json()) as ResearchTaskView;
    },
  });
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        `/api/research/tasks/${encodeURIComponent(taskId)}`,
        { method: 'DELETE', credentials: 'same-origin' },
      );
      if (!response.ok) {
        throw new ResearchTaskAccessError(response.status);
      }
    },
  });

  const requestCancel = async () => {
    try {
      const next = await cancelMutation.mutateAsync();
      await queryClient.cancelQueries({ queryKey, exact: true });
      queryClient.setQueryData(queryKey, next);
      setActionError('');
    } catch (error) {
      setActionError(accessErrorMessage(error, label.login, label.unavailable));
    }
  };
  const requestDelete = async () => {
    try {
      await deleteMutation.mutateAsync();
      await queryClient.cancelQueries({ queryKey, exact: true });
      setDeleted(true);
      queryClient.setQueryData(queryKey, {
        task_id: taskId,
        status: 'deleted',
      });
      setActionError('');
    } catch (error) {
      setActionError(accessErrorMessage(error, label.login, label.unavailable));
    }
  };

  const task = taskQuery.data ?? { task_id: taskId, status: 'queued' };
  const accessError =
    taskQuery.error instanceof ResearchTaskAccessError
      ? accessErrorMessage(taskQuery.error, label.login, label.unavailable)
      : '';

  return {
    task,
    cancelBusy: cancelMutation.isPending,
    deleteBusy: deleteMutation.isPending,
    deleted,
    loadError: accessError,
    actionError,
    statusUpdating: Boolean(taskQuery.error && !accessError),
    requestCancel,
    requestDelete,
  };
}

export const ResearchTaskQueryProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [queryClient] = useState(() => new QueryClient());
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
